/**
 * @file The group filter as editable rows, one per filter, for the group editor. Each row becomes a
 * condition from condition.js, so a group saved here and one built in the Customers filter write the
 * same core-service keys.
 */
import { merge } from 'lodash-es'
import moment from 'moment'
import {
  AccountManagerCondition,
  AccountStatusCondition,
  AccountTagsCondition,
  AiCallsEnabledCondition,
  AutopayCondition,
  BirthdayCondition,
  BirthdayMonthCondition,
  Condition,
  ConfirmedPromiseCondition,
  CreatedDateRangeCondition,
  DeclinedPaymentsCondition,
  DueDateCondition,
  EmailEnabledCondition,
  FollowUpAssignedToCondition,
  FollowUpDateRangeCondition,
  FollowUpStatusCondition,
  LastNameCondition,
  LocationCondition,
  OverdueCondition,
  PromiseDateRangeCondition,
  RegisteredCustomerCondition,
  ScheduledPaymentCondition,
  ScheduledPaymentDateRangeCondition,
  TextEnabledCondition,
  UnConfirmedPromiseCondition,
  UnregisteredCustomerCondition,
  dateFromToday,
  parseFilter
} from '@/views/MessageCenter/condition.js'
import { DATE_FORMAT } from '@/views/Groups/schedule'

export { DATE_FORMAT }
export const FOLLOW_UP_STATUS_OPTIONS = Object.entries(
  FollowUpStatusCondition.Labels
).map(([value, text]) => ({ value, text }))

export function daysFromToday(date) {
  return moment(date).startOf('day').diff(moment().startOf('day'), 'days')
}

// The date filters' presets. Core stores date filters as days from today, so a preset is a fixed
// [low, high] offset; only Birthday has calendar months (core's birthday_month), as `month`.
const TODAY = { value: 'today', text: 'Today', range: [0, 0] }
const TOMORROW = { value: 'tomorrow', text: 'Tomorrow', range: [1, 1] }
const YESTERDAY = { value: 'yesterday', text: 'Yesterday', range: [-1, -1] }
const NEXT_7 = { value: 'next_7', text: 'Next 7 days', range: [0, 6] }
const NEXT_30 = { value: 'next_30', text: 'Next 30 days', range: [0, 29] }
const LAST_7 = { value: 'last_7', text: 'Last 7 days', range: [-6, 0] }
const LAST_30 = { value: 'last_30', text: 'Last 30 days', range: [-29, 0] }
const LAST_90 = { value: 'last_90', text: 'Last 90 days', range: [-89, 0] }
const THIS_MONTH = { value: 'this_month', text: 'This month', month: 'this' }
const NEXT_MONTH = { value: 'next_month', text: 'Next month', month: 'next' }

export const CUSTOM_RANGE = 'custom'
export const DATE_PRESETS = {
  birthday: [TODAY, TOMORROW, NEXT_7, THIS_MONTH, NEXT_MONTH],
  created: [TODAY, YESTERDAY, LAST_7, LAST_30, LAST_90],
  promise_date: [TODAY, TOMORROW, NEXT_7, NEXT_30, YESTERDAY, LAST_7],
  scheduled_date: [TODAY, TOMORROW, NEXT_7, NEXT_30, YESTERDAY, LAST_7],
  follow_up_date: [TODAY, TOMORROW, NEXT_7, NEXT_30, YESTERDAY, LAST_7]
}

/** The preset whose offsets are exactly [low, high], or null. */
export function presetFor(field, low, high) {
  return (
    DATE_PRESETS[field]?.find(
      ({ range }) => range[0] === low && range[1] === high
    )?.value ?? null
  )
}

/** The row's preset, or undefined when it has none (or a custom range). */
function rowPreset(row) {
  return DATE_PRESETS[row.field]?.find(p => p.value === row.preset)
}

/** A date row's [low, high] day offsets: its preset's, else its custom dates', else null. */
export function dateRowRange(row) {
  const preset = rowPreset(row)
  if (preset) return preset.range ?? null
  const from = row.from || row.to
  if (!from) return null

  return [daysFromToday(from), daysFromToday(row.to || row.from)]
}

export function isSet(value) {
  return value !== null && value !== undefined && value !== ''
}

const TOGGLES = {
  autopay: has => new AutopayCondition(has),
  scheduled_payment: has => new ScheduledPaymentCondition(has),
  confirmed_promise: has => new ConfirmedPromiseCondition(has),
  unconfirmed_promise: has => new UnConfirmedPromiseCondition(has),
  registration: has =>
    has
      ? new RegisteredCustomerCondition()
      : new UnregisteredCustomerCondition(),
  text_enabled: has => new TextEnabledCondition(has),
  email_enabled: has => new EmailEnabledCondition(has),
  ai_calls_enabled: has => new AiCallsEnabledCondition(has)
}

/** The Has / Doesn't have condition for one toggle option, e.g. ('autopay', false). */
export function toggleCondition(option, has) {
  return TOGGLES[option]?.(has) ?? null
}

// Condition type → [toggle option, whether the condition means "has"].
const TOGGLE_BY_TYPE = {
  [Condition.Type.Autopay]: c => ['autopay', c.value !== false],
  [Condition.Type.ScheduledPayment]: c => [
    'scheduled_payment',
    c.value !== false
  ],
  [Condition.Type.ConfirmedPromises]: c => [
    'confirmed_promise',
    c.value !== false
  ],
  [Condition.Type.UnConfirmedPromises]: c => [
    'unconfirmed_promise',
    c.value !== false
  ],
  [Condition.Type.RegisteredCustomer]: () => ['registration', true],
  [Condition.Type.UnregisteredCustomer]: () => ['registration', false],
  [Condition.Type.TextEnabled]: c => ['text_enabled', c.value !== false],
  [Condition.Type.EmailEnabled]: c => ['email_enabled', c.value !== false],
  [Condition.Type.AiCallsEnabled]: c => ['ai_calls_enabled', c.value !== false]
}

/** The toggle option and Has/Doesn't-have for a condition, or null when it is not a toggle. */
export function toggleFromCondition(condition) {
  return TOGGLE_BY_TYPE[condition.type]?.(condition) ?? null
}

/** Every filter the Customers filter offers, in its order. `kind` picks the row's inputs. */
export const FILTER_FIELDS = [
  {
    value: 'locations',
    text: 'Locations',
    kind: 'locations',
    group: 'Account'
  },
  {
    value: 'account_managers',
    text: 'Account manager',
    kind: 'managers',
    group: 'Account'
  },
  {
    value: 'follow_up_assigned_to',
    text: 'Follow-up assigned to',
    kind: 'follow_up_managers',
    group: 'Account'
  },
  { value: 'account_tags', text: 'Tags', kind: 'tags', group: 'Account' },
  {
    value: 'account_status',
    text: 'Account status',
    kind: 'statuses',
    group: 'Account'
  },
  {
    value: 'last_name',
    text: 'Last name range',
    kind: 'letters',
    group: 'Account'
  },
  {
    value: 'overdue',
    text: 'Days past due',
    kind: 'days',
    group: 'Balances & Dates'
  },
  {
    value: 'due',
    text: 'Days until due date',
    kind: 'days',
    group: 'Balances & Dates'
  },
  {
    value: 'promise_date',
    text: 'Promise to pay date',
    kind: 'dates',
    group: 'Balances & Dates'
  },
  {
    value: 'birthday',
    text: 'Birthday',
    kind: 'birthday',
    group: 'Balances & Dates'
  },
  {
    value: 'created',
    text: 'Created',
    kind: 'dates',
    group: 'Balances & Dates'
  },
  {
    value: 'scheduled_date',
    text: 'Scheduled payment date',
    kind: 'dates',
    group: 'Balances & Dates'
  },
  {
    value: 'declined',
    text: 'Declined payments',
    kind: 'declined',
    group: 'Balances & Dates'
  },
  {
    value: 'follow_up_date',
    text: 'Follow-up date',
    kind: 'dates',
    group: 'Balances & Dates'
  },
  {
    value: 'follow_up_status',
    text: 'Follow-up status',
    kind: 'follow_up_statuses',
    group: 'Balances & Dates'
  },
  {
    value: 'autopay',
    text: 'Autopay',
    kind: 'toggle',
    group: 'Payment & Contact'
  },
  {
    value: 'scheduled_payment',
    text: 'Scheduled payment',
    kind: 'toggle',
    group: 'Payment & Contact'
  },
  {
    value: 'confirmed_promise',
    text: 'Confirmed promise',
    kind: 'toggle',
    group: 'Payment & Contact'
  },
  {
    value: 'unconfirmed_promise',
    text: 'Unconfirmed promise',
    kind: 'toggle',
    group: 'Payment & Contact'
  },
  {
    value: 'registration',
    text: 'Registration',
    kind: 'toggle',
    group: 'Payment & Contact'
  },
  {
    value: 'text_enabled',
    text: 'Text enabled',
    kind: 'toggle',
    group: 'Payment & Contact'
  },
  {
    value: 'email_enabled',
    text: 'Email enabled',
    kind: 'toggle',
    group: 'Payment & Contact'
  },
  {
    value: 'ai_calls_enabled',
    text: 'AI calls enabled',
    kind: 'toggle',
    group: 'Payment & Contact'
  }
]

export function fieldKind(field) {
  return FILTER_FIELDS.find(option => option.value === field)?.kind ?? null
}

let rowKey = 0

/** A blank row for `field`; every row carries every value slot so Vue tracks them all. */
export function emptyRow(field) {
  return {
    key: ++rowKey,
    field,
    min: null,
    max: null,
    from: null,
    to: null,
    preset: null,
    items: [],
    days: null,
    has: true
  }
}

const DATE_CONDITIONS = {
  promise_date: PromiseDateRangeCondition,
  created: CreatedDateRangeCondition,
  scheduled_date: ScheduledPaymentDateRangeCondition,
  birthday: BirthdayCondition,
  follow_up_date: FollowUpDateRangeCondition
}

/**
 * A date row's condition: a preset moves with each day; picked dates stay put. A range saved as
 * offsets before fixed dates existed keeps moving until its dates are changed.
 */
function dateCondition(row) {
  const Range = DATE_CONDITIONS[row.field]
  const preset = rowPreset(row)
  if (preset?.range) return new Range(...preset.range)
  const from = row.from || row.to
  if (!from) return null
  const to = row.to || row.from
  const { offsets } = row
  if (offsets && offsets.from === from && offsets.to === to) {
    return new Range(...offsets.range)
  }
  // A birthday comes round every year: only its month and day are kept.
  const day = date => (row.field === 'birthday' ? date.slice(5) : date)

  return Range.on(day(from), day(to))
}

/** The condition one row stands for, or null while the row is still empty. */
export function rowCondition(row) {
  const kind = fieldKind(row.field)
  const items = row.items ?? []
  switch (kind) {
    case 'locations':
      return items.length ? new LocationCondition(items) : null
    case 'managers':
      return items.length ? new AccountManagerCondition(items) : null
    case 'tags':
      return items.length ? new AccountTagsCondition(items) : null
    case 'statuses':
      return items.length ? new AccountStatusCondition(items) : null
    case 'follow_up_managers':
      return items.length ? new FollowUpAssignedToCondition(items) : null
    case 'follow_up_statuses':
      return items.length ? new FollowUpStatusCondition(items) : null
    case 'letters':
      return row.from || row.to ? new LastNameCondition(row.from, row.to) : null
    case 'days':
      return dayCondition(row)
    case 'dates':
      return dateCondition(row)
    case 'birthday': {
      const month = rowPreset(row)?.month

      return month ? new BirthdayMonthCondition(month) : dateCondition(row)
    }
    // On once Days is set; the picked origins narrow it, and none picked is any origin.
    case 'declined':
      if (!isSet(row.days)) return null

      return new DeclinedPaymentsCondition(items, Math.max(Number(row.days), 0))
    case 'toggle':
      return toggleCondition(row.field, row.has !== false)
    default:
      return null
  }
}

function dayCondition(row) {
  if (row.field === 'overdue') {
    if (!row.min && !row.max) return null

    return new OverdueCondition(row.min || null, row.max || null)
  }
  // core-service needs both ends of due_date_range; one value means that one day.
  const low = isSet(row.min) ? row.min : row.max
  const high = isSet(row.max) ? row.max : row.min
  if (!isSet(low)) return null

  return new DueDateCondition(low, high)
}

/** The saved-filter JSON for the rows: the merged `filter` of every row's condition. */
export function filterFromRows(rows) {
  return merge(
    {},
    ...rows.map(rowCondition).map(condition => condition?.filter ?? {})
  )
}

const rowItems = row => row.items ?? []

/** Why the rows cannot be saved, or null. Only what core-service would reject. */
export function rowsProblem(rows) {
  // Only rows holding a value count: the Customers filter always has an (often empty) row for both.
  const used = rows.filter(row => rowCondition(row)).map(row => row.field)
  if (used.includes('overdue') && used.includes('due')) {
    return 'Use either days past due or days until due, not both.'
  }
  for (const row of rows) {
    if (row.field === 'declined' && rowItems(row).length && !isSet(row.days)) {
      return 'Enter how many days back to look for declined payments.'
    }
    if (row.field === 'birthday' && row.preset === CUSTOM_RANGE) {
      const range = dateRowRange(row)
      // Ends a whole year apart share a month-day, which would save as that one day.
      if (range && range[1] - range[0] > 364) {
        return 'Pick a birthday range of a year or less.'
      }
    }
  }

  return null
}

function dateRow(field, condition) {
  if (condition.dates) {
    const [from, to] = condition.dates

    return {
      ...emptyRow(field),
      preset: CUSTOM_RANGE,
      ...(field === 'birthday' ? birthdayCalendarDates(from, to) : { from, to })
    }
  }
  const preset = presetFor(field, condition.low, condition.high)
  if (preset) return { ...emptyRow(field), preset }
  const from = dateFromToday(condition.low).format(DATE_FORMAT)
  const to = dateFromToday(condition.high).format(DATE_FORMAT)

  return {
    ...emptyRow(field),
    preset: CUSTOM_RANGE,
    from,
    to,
    offsets: { from, to, range: [condition.low, condition.high] }
  }
}

/** A yearly MM-DD birthday range on this year's calendar, running into next January across the new year. */
function birthdayCalendarDates(fromMonthDay, toMonthDay) {
  const on = (monthDay, year) => {
    const [month, day] = monthDay.split('-').map(Number)
    const date = moment({ year, month: month - 1, date: 1 })

    return date.date(Math.min(day, date.daysInMonth()))
  }
  const from = on(fromMonthDay, moment().year())
  let to = on(toMonthDay, from.year())
  if (to.isBefore(from)) to = on(toMonthDay, from.year() + 1)

  return { from: from.format(DATE_FORMAT), to: to.format(DATE_FORMAT) }
}

const ROW_BY_TYPE = {
  [Condition.Type.Location]: c => ({
    ...emptyRow('locations'),
    items: c.locations
  }),
  [Condition.Type.AccountManager]: c => ({
    ...emptyRow('account_managers'),
    items: c.accountManagers
  }),
  [Condition.Type.AccountTags]: c => {
    const slugs = (c.slugs ?? []).filter(Boolean)

    return slugs.length ? { ...emptyRow('account_tags'), items: slugs } : null
  },
  [Condition.Type.AccountStatus]: c => ({
    ...emptyRow('account_status'),
    items: c.statuses
  }),
  [Condition.Type.LastName]: c => ({
    ...emptyRow('last_name'),
    from: c.start,
    to: c.end
  }),
  [Condition.Type.Overdue]: c => ({
    ...emptyRow('overdue'),
    min: c.min ?? null,
    max: c.max ?? null
  }),
  [Condition.Type.DueDate]: c => ({
    ...emptyRow('due'),
    min: c.min,
    max: c.max ?? c.min
  }),
  [Condition.Type.PromiseDateRange]: c => dateRow('promise_date', c),
  [Condition.Type.CreatedDateRange]: c => dateRow('created', c),
  [Condition.Type.ScheduledPaymentDateRange]: c => dateRow('scheduled_date', c),
  [Condition.Type.Birthday]: c => dateRow('birthday', c),
  [Condition.Type.BirthdayMonth]: c => ({
    ...emptyRow('birthday'),
    preset: DATE_PRESETS.birthday.find(p => p.month === c.month).value
  }),
  [Condition.Type.DeclinedPayments]: c => ({
    ...emptyRow('declined'),
    items: c.origins,
    days: c.days
  }),
  [Condition.Type.FollowUpAssignedTo]: c => ({
    ...emptyRow('follow_up_assigned_to'),
    items: c.assignees
  }),
  [Condition.Type.FollowUpDateRange]: c => dateRow('follow_up_date', c),
  [Condition.Type.FollowUpStatus]: c => ({
    ...emptyRow('follow_up_status'),
    items: c.statuses
  })
}

/** Rows for a saved filter, one per filter it uses. Keys the editor has no row for are dropped. */
export function rowsFromFilter(filter) {
  return parseFilter(filter ?? {})
    .map(condition => {
      const toggle = toggleFromCondition(condition)
      if (toggle) return { ...emptyRow(toggle[0]), has: toggle[1] }

      return ROW_BY_TYPE[condition.type]?.(condition) ?? null
    })
    .filter(Boolean)
}
